import { ActivityReasonCategory } from 'types/activity'

export const ACTIVITY_LOG_SOURCE = 'admin-web-client'

/** Human-readable label for EPS `qle_*` actions; other actions pass through unchanged. */
export function formatActivityActionLabel(action: string): string {
  if (!action.startsWith('qle_')) {
    return action
  }

  return action
    .split('_')
    .map((segment) =>
      segment === 'qle'
        ? 'QLE'
        : segment.charAt(0).toUpperCase() + segment.slice(1)
    )
    .join(' ')
}

/** EPS persists these on QLE HTTP writes (EMS-10329). Filter `value` must match DB `action`. */
export const QLE_EPS_ACTIVITY_ACTIONS = [
  'qle_created',
  'qle_change_detail_updated',
  'qle_document_upload_started',
  'qle_document_upload_completed',
  'qle_submitter_action_requested',
  'qle_submitter_action_email_resent',
  'qle_submitter_action_completed',
  'qle_status_updated',
  'qle_status_batch_updated',
  'qle_assignee_updated',
  'qle_assignee_batch_updated',
  'qle_group_linked',
  'qle_group_unlinked',
  'qle_preview_confirmed',
  'qle_group_created',
  'qle_group_url_extended',
  'qle_group_application_created',
] as const

export const ActivityLogAction = {
  BULK_UPDATE_APPLICATIONS_ASSIGNEE: 'Bulk Update Applications Assignee',
  BULK_CLEAR_ESCALATION_FLAGS: 'Bulk Clear Escalation Flags',
  BULK_UPDATE_APPLICATIONS: 'Bulk Update Applications',
  CSV_UPDATE_APPLICATIONS: 'CSV Update Applications',
  BULK_RENEWAL_APPLICATIONS_UPDATE: 'Bulk Renewal Applications Update',
  BULK_UPDATE_EMPLOYEES: 'Bulk Update Employees',
  PAYMENT_BULK_UPLOAD: 'Payment Bulk Upload',
  EDIT_WORKER_INFO: 'Edit Worker Info',
  ADD_WORKER: 'Add Employee',
  LOGIN_AS_WORKER: 'Login as Worker',
  LOGIN_TO_QLE_AS_WORKER: 'Login to QLE as Worker',
  UNLOCK_CART: 'Unlock Cart',
  RESET_SIGN_IN_CREDS: 'Reset Sign In Credentials',
  RESET_ACCOUNT: 'Reset Account',
  EDIT_ENROLLMENT: 'Edit Enrollment',
  OPEN_MEDICARE_ENROLLMENT: 'Open Medicare Enrollment',
  CLOSE_MEDICARE_ENROLLMENT: 'Close Medicare Enrollment',
  APPROVE_MEDICARE_ENROLLMENT: 'Approve Medicare Enrollment',
  ADD_MEDICARE_PLAN: 'Add Medicare Plan',
  DELETE_MEDICARE_PLAN: 'Delete Medicare Plan',
  EDIT_COBRA_COVERAGE: 'Edit COBRA Coverage',
  SEND_ICHRANOTICE: 'Send ICHRA Notice',
  SEND_WELCOME_EMAIL: 'Send Welcome Email',
  STOP_COVERAGE: 'Stop Coverage',
  ADD_CHANGE_EVENT: 'Add Change Event',
  GENERATE_VCN: 'Generate VCN',
  REUSE_VCN: 'Reuse VCN',
  SUBMIT_VCN_ACTION: 'Submit VCN Action',
  SEND_REMINDER_EMAIL: 'Send Reminder Email',
  BENEFIT_INTEGRATION_SEND_TO_HEALTH_SHERPA: 'Send to HealthSherpa',
  EDIT_EMPLOYEE_BENEFITS_DATE: 'Edit Employee Benefits Date',
  EDIT_ANCILLARY_BENEFIT_DATE: 'Edit Ancillary Benefit Date',
  VOID_ANCILLARY_BENEFIT: 'Void Ancillary Benefit',
  ASSIGN_APPLICATIONS_TO_ASSIGNEE: 'Assign Applications to Assignee',
  SURECO_SUBMIT_APPLICATION: 'SureCo Submit Application',
  REMOVE_ASSIGNEE: 'Remove Assignee',
  CENSUS_REVIEW_APPROVE: 'Census Review Approve Records',
  CENSUS_REVIEW_REJECT: 'Census Review Reject Records',
  CENSUS_REVIEW_SEND_TO_ZENDESK: 'Census Review Send to Zendesk',
  UPDATE_HRIS_PAYROLL_CONFIGURATION: 'Update HRIS/Payroll Configuration',
  UPDATE_CUSTOMER_CONFIGURATION: 'Update Customer Configuration',
  UPDATE_APPLICATION_ESCALATION: 'Update Application Escalation',
  DOWNLOAD_APPLICATIONS: 'Download Applications',
}

/**
 * Customer Configuration toggle keys (Admin › Customer › Company tab) → labels.
 * Single source of truth for both the log writer and the history view.
 */
export const CUSTOMER_CONFIG_LABELS = {
  isIchraNoticeEnabled: 'ICHRA Notices',
  isWelcomeEmailEnabled: 'Welcome Email',
  isEnrollmentReminderEmailEnabled: 'Reminder Emails',
  isEnrollmentConfirmationEmailEnabled: 'Enrollment Confirmation Emails',
  isApplicationVerifiedEmailEnabled: 'Application Verified Emails',
  isCMv1Enabled: 'CMv1 Navigation Link',
  isCMv2Enabled: 'CMv2 Navigation Link',
  isCMv2APIEnabled: 'CMv2 API',
  displayContributionsToEmployees: 'Display ER Contribution',
} as const

export type CustomerConfigKey = keyof typeof CUSTOMER_CONFIG_LABELS

export enum ENSActivityLogAction {
  ADD_DEPENDENT = 'Add Dependent',
  EDIT_DEPENDENT = 'Edit Dependent',
  DELETE_DEPENDENT = 'Delete Dependent',
  SAVE_AND_CONTINUE_DEPENDENTS = 'Save & Continue Dependents',
  EDIT_PERSONAL_INFO = 'Edit Personal Info',
  ADD_PROVIDER = 'Add Provider',
  REMOVE_PROVIDER = 'Remove Provider',
  REMOVE_ALL_FROM_PROVIDER = 'Remove All From Provider',
}

export enum ENSShopForHealthAction {
  ShopForHealthViewed = 'Shop For Health Viewed',
  SelectShopForHealthGroup = 'Select Shop For Health Group',
  DataLoaded = 'Shop For Health Data Loaded',
  NavigateBundle = 'Navigate Bundle',
  ToggleCompare = 'Toggle Compare',
  CompareBundles = 'Compare Bundles',
  MyBenefits = 'Add/Remove My Benefits plan',
  Filter = 'Change Filter',
  OpenBenefitsSummary = 'Open Benefits Summary',
  ToggleBundleCardInfo = 'Toggle Bundle Card Info',
  AddProvider = 'Clicked the Add Provider button',
  AddDentalCoverage = 'Add Dental Coverage',
  RemoveDentalCoverage = 'Remove Dental Coverage',
  EditDentalCoveragePersons = 'Edit Dental Coverage Persons',
  ClearCart = 'Clear Cart',
  DeclineDentalCoverage = 'Decline Dental Coverage',
  EditVisionCoveragePersons = 'Edit Vision Coverage Persons',
  DeclineVisionCoverage = 'Decline Vision Coverage',
  AddBasicLifeCoverage = 'Add Basic Life Coverage',
  DeclineBasicLifeCoverage = 'Decline Basic Life Coverage',
  AddOrEditBeneficiaries = 'Add/Edit Beneficiaries',
  DeclineLongTermDisabilityCoverage = 'Decline Long Term Disability Coverage',
  AddLongTermDisabilityCoverage = 'Add Long Term Disability Coverage',
  DeclineCoverage = 'Decline Coverage',
  AddPrimaryCarePhysician = 'Add Primary Care Physician',
  SkipPrimaryCarePhysician = 'Skip Primary Care Physician',
  ConfirmElections = 'Confirm Elections',
  DeclineMedicalCoverage = 'Decline Medical Coverage',
  DeclineBenefitTypeHealth = 'Decline Benefit Type Health',
  AddVisionChoiceCoverage = 'Add Vision Choice Coverage',
  RemoveVisionChoiceCoverage = 'Remove Vision Choice Coverage',
}

export const ACTION_OPTIONS = Array.from(
  new Set([
    'Page View',
    'Login',
    'Navigate Bundle',
    'Shop For Health Data Loaded',
    'Submit HRIS Census Report',
    'Select Shop For Health Group',
    'Add/Remove My Benefits plan',
    'noyo.snapshot.request.started',
    'VCN Card Retrieved',
    'FlexpaConsentClicked',
    'noyo.snapshot.status.updated',
    'Shop For Health Viewed',
    'HRIS - NewlyIneligibleIdentifier detected',
    'Upload Census File V2',
    'Change Filter',
    'noyo.snapshot.submitted',
    'HRIS - AddressIdentifier detected',
    'noyo.snapshot.status.discrepancy',
    'Toggle Bundle Card Info',
    'Updated census file rows',
    'HRIS - 1 new hire(s) detected',
    'Viewed Census Management',
    'Toggle Compare',
    'Submit census file rows for review',
    'Create new employee from Census file',
    'Submit HRIS Census Generated File to Census V1',
    'HRIS - IncomeUpdateIdentifier detected',
    'HRIS - IncomeUpdateIdentifier detected with affordability issue',
    'Changed Medicare Benefit',
    'Toggle Filter',
    'Payroll Deduction Created',
    'HRIS - TerminationIdentifier detected',
    'NOYO_SYNC_REQUEST_START',
    'Withdraw census file rows',
    'HRIS - NewlyEligibleIdentifier detected',
    'VCN Card Created',
    'Create member update Zendesk ticket',
    'Rejected census file rows',
    'Clicked the Add Provider button',
    'FlexpaRefreshClicked',
    'FlexpaConsentClosed',
    'USER_UPDATED_PROFILE',
    'NOYO_SYNC_REQUEST_SUBMITTED',
    'Edit Employee',
    'noyo.plan.noncbora.terminated',
    'UnlockCart',
    ...Object.values(ActivityLogAction),
    ...QLE_EPS_ACTIVITY_ACTIONS,
    ...Object.values(ENSActivityLogAction),
    ...Object.values(ENSShopForHealthAction),
  ])
)
  .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  .map((action, index) => ({
    key: `action-${index}`,
    value: action,
    label: action.startsWith('qle_')
      ? formatActivityActionLabel(action)
      : action,
  }))

export const ReasonCategories = [
  { value: ActivityReasonCategory.QLE, label: 'QLE' },
  { value: ActivityReasonCategory.ER_REQUEST, label: 'ER request' },
  { value: ActivityReasonCategory.EE_REQUEST, label: 'EE request' },
  { value: ActivityReasonCategory.CLEANUP, label: 'Cleanup' },
  { value: ActivityReasonCategory.OTHER, label: 'Other' },
]
